import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { computed, signal } from '@preact/signals-react';
import type {
    Condition,
    QuantitativeCondition,
    SelfAliasIndicatorFormula,
} from '@thinkalpha/platform-ws-client/contracts/ideas/conditions.js';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';

export type RankDirection = 'top' | 'bottom';

export interface RankStepState {
    readonly id: string;
    readonly direction: RankDirection;
    readonly count: number | null;
    readonly bySelection: SelfAliasIndicatorFormula | null;
}

interface RankStepsModelState {
    readonly steps: ReadonlyArray<RankStepState>;
}

type RankStepsAction =
    | { type: 'ADD'; step: RankStepState }
    | { type: 'REMOVE'; id: string }
    | { type: 'SET_DIRECTION'; id: string; direction: RankDirection }
    | { type: 'SET_COUNT'; id: string; count: number | null }
    | { type: 'SET_BY'; id: string; bySelection: SelfAliasIndicatorFormula | null }
    | { type: 'MOVE'; id: string; delta: -1 | 1 }
    | { type: 'SET_STEPS'; steps: ReadonlyArray<RankStepState> };

function reducer(state: RankStepsModelState, action: RankStepsAction): RankStepsModelState {
    switch (action.type) {
        case 'ADD':
            return { steps: [...state.steps, action.step] };
        case 'REMOVE': {
            const next = state.steps.filter((s) => s.id !== action.id);
            return next.length === state.steps.length ? state : { steps: next };
        }
        case 'SET_DIRECTION':
            return updateStep(state, action.id, (s) => ({ ...s, direction: action.direction }));
        case 'SET_COUNT':
            return updateStep(state, action.id, (s) => ({ ...s, count: action.count }));
        case 'SET_BY':
            return updateStep(state, action.id, (s) => ({ ...s, bySelection: action.bySelection }));
        case 'MOVE': {
            const index = state.steps.findIndex((s) => s.id === action.id);
            if (index < 0) return state;
            const target = index + action.delta;
            if (target < 0 || target >= state.steps.length) return state;
            const next = state.steps.slice();
            const [moved] = next.splice(index, 1);
            next.splice(target, 0, moved);
            return { steps: next };
        }
        case 'SET_STEPS':
            return { steps: action.steps };
        default:
            throw new UnreachableCaseError(action);
    }
}

function updateStep(
    state: RankStepsModelState,
    id: string,
    update: (s: RankStepState) => RankStepState,
): RankStepsModelState {
    const index = state.steps.findIndex((s) => s.id === id);
    if (index < 0) return state;
    const current = state.steps[index];
    const next = update(current);
    if (next === current) return state;
    const out = state.steps.slice();
    out[index] = next;
    return { steps: out };
}

function makeStepId(): string {
    return crypto.randomUUID();
}

/**
 * Ordered list of "Top N / Bottom N by <indicator>" rank steps appended to a
 * universe plan. Each step is a `QuantitativeCondition` on the wire — its
 * `indicator.id` is one of the slug-resolved rank indicators
 * (`symbol::topRank` / `symbol::bottomRank`), `selection` is the "by" formula,
 * and `max.value` is the count N (rank <= N keeps the top/bottom N).
 *
 * The model is held by `UniverseCreationModel` as an owned sub-model. Ill-
 * formed steps (missing count / missing by) surface validation errors and are
 * dropped from the wire output of `toWireConditions` so the saved plan never
 * contains a half-formed rank step.
 */
export class RankStepsModel implements Disposable {
    readonly #state: Signal<RankStepsModelState>;

    readonly state: ReadonlySignal<ReadonlyArray<RankStepState>>;
    readonly validationErrors: ReadonlySignal<string[]>;
    readonly isValid: ReadonlySignal<boolean>;

    constructor(initial: ReadonlyArray<RankStepState> = []) {
        this.#state = signal<RankStepsModelState>({ steps: initial });

        this.state = computed(() => this.#state.value.steps);
        this.validationErrors = computed(() => {
            const errors: string[] = [];
            this.#state.value.steps.forEach((step, i) => {
                const stepNumber = i + 1;
                if (step.count == null || step.count <= 0) {
                    errors.push(`Ranking step ${stepNumber}: enter a positive count`);
                }
                if (step.bySelection == null) {
                    errors.push(`Ranking step ${stepNumber}: choose what to rank by`);
                }
            });
            return errors;
        });
        this.isValid = computed(() => this.validationErrors.value.length === 0);
    }

    #dispatch(action: RankStepsAction): void {
        this.#state.value = reducer(this.#state.value, action);
    }

    add(direction: RankDirection): void {
        this.#dispatch({
            type: 'ADD',
            step: {
                id: makeStepId(),
                direction,
                count: null,
                bySelection: null,
            },
        });
    }

    remove(id: string): void {
        this.#dispatch({ type: 'REMOVE', id });
    }

    setDirection(id: string, direction: RankDirection): void {
        this.#dispatch({ type: 'SET_DIRECTION', id, direction });
    }

    setCount(id: string, count: number | null): void {
        this.#dispatch({ type: 'SET_COUNT', id, count });
    }

    setBy(id: string, bySelection: SelfAliasIndicatorFormula | null): void {
        this.#dispatch({ type: 'SET_BY', id, bySelection });
    }

    move(id: string, delta: -1 | 1): void {
        this.#dispatch({ type: 'MOVE', id, delta });
    }

    clear(): void {
        if (this.#state.value.steps.length === 0) return;
        this.#dispatch({ type: 'SET_STEPS', steps: [] });
    }

    setSteps(steps: ReadonlyArray<RankStepState>): void {
        this.#dispatch({ type: 'SET_STEPS', steps });
    }

    /**
     * Serializes well-formed steps into `QuantitativeCondition`s. Ill-formed
     * steps (no count / no by) are excluded — `validationErrors` keeps them
     * visible in the UI, but the saved plan never carries them. Steps whose
     * direction's rank-indicator id is missing are also skipped individually
     * rather than dropping the whole rank-step list.
     */
    toWireConditions(topRankId?: string, bottomRankId?: string): QuantitativeCondition[] {
        const out: QuantitativeCondition[] = [];
        for (const step of this.#state.value.steps) {
            if (step.count == null || step.count <= 0) continue;
            if (step.bySelection == null) continue;
            const indicatorId = step.direction === 'top' ? topRankId : bottomRankId;
            if (indicatorId == null) continue;
            out.push({
                type: 'quantitative',
                id: step.id,
                enabled: true,
                indicator: { id: indicatorId },
                selection: step.bySelection,
                min: null,
                max: { value: step.count, mode: 'inclusive' },
            });
        }
        return out;
    }

    [Symbol.dispose](): void {
        // No subscriptions; state holds plain values.
    }
}

/**
 * Splits an incoming wire conditions list into rank-step conditions vs. the
 * rest. Order within each bucket is preserved.
 */
export function partitionRankConditions(
    conditions: ReadonlyArray<Condition>,
    topRankId: string | undefined,
    bottomRankId: string | undefined,
): { rankSteps: RankStepState[]; others: Condition[] } {
    if (topRankId == null && bottomRankId == null) {
        return { rankSteps: [], others: [...conditions] };
    }
    const rankSteps: RankStepState[] = [];
    const others: Condition[] = [];
    for (const cond of conditions) {
        const step = wireToRankStep(cond, topRankId, bottomRankId);
        if (step != null) {
            rankSteps.push(step);
        } else {
            others.push(cond);
        }
    }
    return { rankSteps, others };
}

function wireToRankStep(
    wire: Condition,
    topRankId: string | undefined,
    bottomRankId: string | undefined,
): RankStepState | null {
    if (wire.type !== 'quantitative') return null;
    const direction: RankDirection | null =
        wire.indicator.id === topRankId ? 'top' : wire.indicator.id === bottomRankId ? 'bottom' : null;
    if (direction == null) return null;
    return {
        id: wire.id,
        direction,
        count: wire.max?.value ?? null,
        bySelection: wire.selection ?? null,
    };
}
